import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Wind, Heart, Eye, Hand, Ear, Users, Activity, Waves, CheckCircle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { useUniversalTracking } from "@/hooks/useUniversalTracking";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface WhoopSnapshot {
  hrv?: number;
  rhr?: number;
  timestamp: string;
}

export const GroundingToolsMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeExercise, setActiveExercise] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [exercisePhase, setExercisePhase] = useState<'pre-rating' | 'active' | 'post-rating' | 'complete'>('pre-rating');
  const [distressBefore, setDistressBefore] = useState(5);
  const [distressAfter, setDistressAfter] = useState(5);
  const [whoopBefore, setWhoopBefore] = useState<WhoopSnapshot | null>(null);
  const [whoopAfter, setWhoopAfter] = useState<WhoopSnapshot | null>(null);
  const { logExercise, isLogging } = useUniversalTracking();

  // Fetch latest WHOOP data if available
  const fetchWhoopSnapshot = async (): Promise<WhoopSnapshot | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data } = await supabase
        .from('whoop_recovery_data')
        .select('hrv_rmssd, resting_heart_rate, recorded_at')
        .eq('user_id', user.id)
        .order('recorded_at', { ascending: false })
        .limit(1)
        .single();

      if (data) {
        return {
          hrv: data.hrv_rmssd ?? undefined,
          rhr: data.resting_heart_rate ?? undefined,
          timestamp: data.recorded_at
        };
      }
      return null;
    } catch {
      return null;
    }
  };

  const exercises = [
    // Polyvagal Theory Based
    {
      id: 'voo',
      name: 'Voo Breathing',
      icon: <Waves className="h-4 w-4" />,
      duration: 90,
      category: 'polyvagal',
      instructions: 'Inhale deeply, then exhale making a deep "voo" sound (like a foghorn). Feel the vibration in your chest and belly. Repeat 6-8 times.',
      steps: ['Deep inhale', 'Exhale "vooooo"', 'Feel vibration', 'Repeat'],
      why: 'Activates the ventral vagus nerve through vocal cord vibration, signalling safety to your nervous system.',
      science: 'Polyvagal Theory (Dr. Stephen Porges): The vagus nerve connects your brain to your body. Vocalisation creates vibrations that directly stimulate the vagus, shifting you from fight/flight into calm engagement.'
    },
    {
      id: 'orienting',
      name: 'Orienting Response',
      icon: <Eye className="h-4 w-4" />,
      duration: 60,
      category: 'somatic',
      instructions: 'Slowly turn your head left, pause. Turn right, pause. Let your eyes naturally find things that feel pleasant or neutral. No rushing.',
      steps: ['Slow head turn left', 'Pause & look', 'Slow head turn right', 'Notice pleasant things'],
      why: 'Helps your nervous system assess safety by completing a natural survival response.',
      science: 'Somatic Experiencing (Dr. Peter Levine): Animals naturally orient to their environment after threat. Modern humans often skip this, keeping the nervous system stuck in alarm. Orienting completes the survival cycle.'
    },
    {
      id: 'social-engagement',
      name: 'Social Engagement',
      icon: <Users className="h-4 w-4" />,
      duration: 45,
      category: 'polyvagal',
      instructions: 'Soften your eyes. Relax your jaw. Allow a gentle, genuine half-smile. Tilt your head slightly as if listening to a friend.',
      steps: ['Soft eyes', 'Relaxed jaw', 'Gentle smile', 'Listening tilt'],
      why: 'Activates the "social engagement system" - facial muscles connected to the ventral vagus.',
      science: 'Polyvagal Theory: Your face and ears are wired to your calming nervous system. By adopting postures of social connection, you signal safety to your brain, even when alone.'
    },
    {
      id: 'pendulation',
      name: 'Pendulation',
      icon: <Activity className="h-4 w-4" />,
      duration: 120,
      category: 'somatic',
      instructions: 'Notice where you feel tension or discomfort. Then find somewhere that feels okay or neutral. Gently swing attention between them. Don\'t force anything.',
      steps: ['Find discomfort', 'Find neutral area', 'Swing attention', 'Notice shifts'],
      why: 'Teaches your nervous system to move between activation and calm, building resilience.',
      science: 'Somatic Experiencing: Trauma gets stuck when the nervous system can\'t complete its natural rhythm. Pendulation restores the capacity to oscillate between states, preventing overwhelm.'
    },
    {
      id: 'humming',
      name: 'Vagal Humming',
      icon: <Ear className="h-4 w-4" />,
      duration: 60,
      category: 'polyvagal',
      instructions: 'Hum a simple tune or just "hmmmm" for 4-5 breaths. Feel the vibration in your throat, face, and chest.',
      steps: ['Inhale', 'Hum on exhale', 'Feel vibrations', 'Repeat'],
      why: 'Humming creates internal vibration that directly tones the vagus nerve.',
      science: 'Research shows humming increases heart rate variability (HRV), a key marker of vagal tone and stress resilience. It also releases nitric oxide, which has calming effects.'
    },
    // Classic Grounding
    {
      id: '478',
      name: '4-7-8 Breathing',
      icon: <Wind className="h-4 w-4" />,
      duration: 60,
      category: 'breathing',
      instructions: 'Breathe in for 4, hold for 7, out for 8. Repeat 4 times.',
      steps: ['In (4)', 'Hold (7)', 'Out (8)', 'Rest'],
      why: 'Extended exhale activates the parasympathetic "rest and digest" response.',
      science: 'Dr. Andrew Weil: The held breath allows oxygen to saturate your blood. The long exhale stimulates the vagus nerve, reducing heart rate and promoting calm.'
    },
    {
      id: 'box',
      name: 'Box Breathing',
      icon: <Heart className="h-4 w-4" />,
      duration: 60,
      category: 'breathing',
      instructions: 'In for 4, hold 4, out 4, hold 4. Repeat.',
      steps: ['In (4)', 'Hold (4)', 'Out (4)', 'Hold (4)'],
      why: 'Creates a sense of control and balance in the nervous system.',
      science: 'Used by Navy SEALs for stress management. Equal intervals engage both branches of the autonomic nervous system, promoting equilibrium.'
    },
    {
      id: '54321',
      name: '5-4-3-2-1 Grounding',
      icon: <Eye className="h-4 w-4" />,
      duration: 90,
      category: 'grounding',
      instructions: 'Name: 5 things you see, 4 you touch, 3 you hear, 2 you smell, 1 you taste.',
      steps: ['See (5)', 'Touch (4)', 'Hear (3)', 'Smell (2)', 'Taste (1)'],
      why: 'Anchors you in present-moment sensory reality, interrupting anxious spirals.',
      science: 'Engages the prefrontal cortex (thinking brain) and redirects attention from the amygdala (alarm centre). Sensory focus creates immediate "now" awareness.'
    },
    {
      id: 'bilateral',
      name: 'Bilateral Tapping',
      icon: <Hand className="h-4 w-4" />,
      duration: 60,
      category: 'somatic',
      instructions: 'Alternate tapping your knees or shoulders. Left, right, left, right. Slow and steady.',
      steps: ['Left', 'Right', 'Left', 'Right', 'Continue...'],
      why: 'Bilateral stimulation helps process and integrate distressing experiences.',
      science: 'Derived from EMDR therapy. Alternating stimulation appears to facilitate communication between brain hemispheres and may help move experiences from "stuck" to processed.'
    }
  ];

  const startExercise = async (exerciseId: string) => {
    setActiveExercise(exerciseId);
    setExercisePhase('pre-rating');
    setDistressBefore(5);
    setDistressAfter(5);
    setProgress(0);
    
    // Capture WHOOP snapshot before exercise
    const whoopData = await fetchWhoopSnapshot();
    setWhoopBefore(whoopData);
  };

  const beginExerciseTimer = () => {
    setExercisePhase('active');
    const exercise = exercises.find(e => e.id === activeExercise);
    if (!exercise) return;

    let currentProgress = 0;
    const interval = setInterval(() => {
      currentProgress += 1;
      setProgress((currentProgress / exercise.duration) * 100);
      
      if (currentProgress >= exercise.duration) {
        clearInterval(interval);
        setExercisePhase('post-rating');
      }
    }, 1000);
  };

  const completeExercise = async () => {
    const exercise = exercises.find(e => e.id === activeExercise);
    if (!exercise) return;

    // Capture WHOOP snapshot after exercise
    const whoopDataAfter = await fetchWhoopSnapshot();
    setWhoopAfter(whoopDataAfter);

    // Log the exercise with tracking
    await logExercise({
      exercise_type: `quick_ground_${exercise.category}`,
      exercise_name: exercise.name,
      category: exercise.category,
      duration_minutes: Math.round(exercise.duration / 60),
      symptoms_before: { distress_level: distressBefore },
      symptoms_after: { distress_level: distressAfter },
      completion_data: {
        exercise_id: exercise.id,
        polyvagal_category: exercise.category,
        whoop_before: whoopBefore,
        whoop_after: whoopDataAfter,
        hrv_change: whoopBefore?.hrv && whoopDataAfter?.hrv 
          ? whoopDataAfter.hrv - whoopBefore.hrv 
          : null
      },
      xp_earned: 15
    });

    setExercisePhase('complete');
    
    // Show improvement if any
    const improvement = distressBefore - distressAfter;
    if (improvement > 0) {
      toast.success(`Distress reduced by ${improvement} points`, {
        description: exercise.category === 'polyvagal' 
          ? 'Your vagus nerve is responding well' 
          : 'Your nervous system is regulating'
      });
    }

    // Reset after showing completion
    setTimeout(() => {
      setActiveExercise(null);
      setExercisePhase('pre-rating');
      setProgress(0);
      setWhoopBefore(null);
      setWhoopAfter(null);
    }, 2000);
  };

  const resetExercise = () => {
    setActiveExercise(null);
    setExercisePhase('pre-rating');
    setProgress(0);
    setWhoopBefore(null);
    setWhoopAfter(null);
  };

  const activeExerciseData = exercises.find(e => e.id === activeExercise);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'polyvagal': return 'bg-primary/10 text-primary';
      case 'somatic': return 'bg-secondary/10 text-secondary';
      case 'breathing': return 'bg-accent/10 text-accent-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'polyvagal': return 'Polyvagal';
      case 'somatic': return 'Somatic';
      case 'breathing': return 'Breathwork';
      default: return 'Grounding';
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="gap-2"
        >
          <Wind className="h-4 w-4" />
          Quick Ground
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96" align="start">
        {activeExercise && exercisePhase === 'pre-rating' ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-foreground">{activeExerciseData?.name}</h3>
              <Button variant="ghost" size="sm" onClick={resetExercise}>Cancel</Button>
            </div>
            
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Before we start, how distressed are you feeling right now?</p>
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Calm</span>
                  <span className="font-medium text-foreground">{distressBefore}/10</span>
                  <span>Very distressed</span>
                </div>
                <Slider
                  value={[distressBefore]}
                  onValueChange={([val]) => setDistressBefore(val)}
                  min={1}
                  max={10}
                  step={1}
                  className="w-full"
                />
              </div>
              
              {whoopBefore && (
                <div className="bg-muted/30 rounded-lg p-2 text-xs">
                  <span className="text-muted-foreground">WHOOP: </span>
                  {whoopBefore.hrv && <span className="text-primary">HRV {whoopBefore.hrv}ms</span>}
                  {whoopBefore.rhr && <span className="text-muted-foreground ml-2">RHR {whoopBefore.rhr}bpm</span>}
                </div>
              )}
            </div>
            
            <Button onClick={beginExerciseTimer} className="w-full">
              Begin Exercise
            </Button>
          </div>
        ) : activeExercise && exercisePhase === 'active' ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-foreground">{activeExerciseData?.name}</h3>
                <Badge variant="secondary" className={getCategoryColor(activeExerciseData?.category || '')}>
                  {getCategoryLabel(activeExerciseData?.category || '')}
                </Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={resetExercise}>Stop</Button>
            </div>
            <p className="text-sm text-muted-foreground">{activeExerciseData?.instructions}</p>
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-center text-muted-foreground">
              {Math.ceil((100 - progress) / 100 * (activeExerciseData?.duration || 60))}s remaining
            </p>
            
            <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg p-3 space-y-1">
              <p className="text-xs font-medium text-foreground">Why this works:</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{activeExerciseData?.why}</p>
            </div>
            
            <div className="bg-muted/30 rounded-lg p-3 space-y-1">
              <p className="text-xs font-medium text-foreground">The Science:</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{activeExerciseData?.science}</p>
            </div>
          </div>
        ) : activeExercise && exercisePhase === 'post-rating' ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              <h3 className="font-medium text-foreground">Exercise Complete</h3>
            </div>
            
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">How distressed are you feeling now?</p>
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Calm</span>
                  <span className="font-medium text-foreground">{distressAfter}/10</span>
                  <span>Very distressed</span>
                </div>
                <Slider
                  value={[distressAfter]}
                  onValueChange={([val]) => setDistressAfter(val)}
                  min={1}
                  max={10}
                  step={1}
                  className="w-full"
                />
              </div>
              
              {distressBefore > distressAfter && (
                <div className="bg-primary/10 rounded-lg p-2 text-xs text-primary">
                  Distress reduced from {distressBefore} → {distressAfter} ({distressBefore - distressAfter} points)
                </div>
              )}
            </div>
            
            <Button onClick={completeExercise} className="w-full" disabled={isLogging}>
              {isLogging ? 'Saving...' : 'Log & Complete (+15 XP)'}
            </Button>
          </div>
        ) : activeExercise && exercisePhase === 'complete' ? (
          <div className="space-y-4 text-center py-4">
            <CheckCircle className="h-12 w-12 text-primary mx-auto" />
            <div>
              <h3 className="font-medium text-foreground">Well done!</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Exercise tracked for correlational insights
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <h3 className="font-medium text-foreground mb-1">Nervous System Regulation</h3>
              <p className="text-xs text-muted-foreground">
                Evidence-based exercises from Polyvagal Theory and Somatic Psychology
              </p>
            </div>
            
            <ScrollArea className="h-80">
              <div className="space-y-2 pr-3">
                {exercises.map((exercise) => (
                  <Card
                    key={exercise.id}
                    className="p-3 cursor-pointer hover:border-primary/50 transition-all"
                    onClick={() => startExercise(exercise.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="shrink-0 mt-0.5 text-primary">
                        {exercise.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-medium text-foreground">{exercise.name}</p>
                          <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${getCategoryColor(exercise.category)}`}>
                            {getCategoryLabel(exercise.category)}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">{exercise.why}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};